<template>
  <div class="main">
    <c-panel>
      <h3 class="u-mb-10">Painel nutricional da merenda</h3>

      <el-row :gutter="10" class="u-mb-20">
        <el-col :xs="24" :sm="10">
          <el-select v-model="filtro.instituicao_id" placeholder="Todas as instituições do meu escopo"
                     filterable clearable style="width:100%" @change="carregar">
            <el-option v-for="i in instituicoes" :key="i.id" :label="i.nome" :value="i.id"></el-option>
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="6" class="u-text-right">
          <el-button @click="carregar" type="info" plain icon="el-icon-refresh">ATUALIZAR</el-button>
        </el-col>
      </el-row>

      <!-- ITENS 12 e 13 -->
      <h4 class="u-mb-10">Alunos</h4>
      <div v-if="alunos && alunos.aviso">
        <el-alert :title="alunos.aviso" type="info" :closable="false" show-icon></el-alert>
      </div>
      <el-row :gutter="10" v-else-if="alunos">
        <el-col :xs="12" :sm="6" v-for="c in cards" :key="c.chave">
          <div class="card-indicador">
            <div class="u-fs-12">{{ c.rotulo }}</div>
            <div class="valor">
              <span v-if="c.valor !== null">{{ c.valor }}</span>
              <em v-else class="u-fs-12">sem dados</em>
            </div>
            <div class="u-fs-12" v-if="c.percentual !== null && c.percentual !== undefined">{{ c.percentual }}%</div>
          </div>
        </el-col>
      </el-row>

      <!-- ITENS 24 e 27 -->
      <h4 class="u-mt-30 u-mb-10">Situação nutricional da rede</h4>

      <div v-if="nutricional && nutricional.situacao === 'sem_dados'">
        <el-alert :title="nutricional.aviso || 'Sem medições antropométricas no recorte.'" type="info" :closable="false" show-icon></el-alert>
      </div>

      <div v-else-if="nutricional">
        <div class="u-fs-12 u-mb-10">
          {{ nutricional.alunos_com_medicao }} aluno(s) com medição · critério: {{ nutricional.criterio }} ·
          referência: {{ nutricional.referencia }}
        </div>

        <el-row :gutter="16">
          <el-col :xs="24" :md="8" v-for="(dados, chave) in nutricional.indicadores" :key="chave">
            <div class="bloco-indicador">
              <h5>{{ rotulos[chave] }}</h5>

              <el-alert v-if="!dados.completo" class="u-mb-10" type="warning" :closable="false" show-icon
                :title="`Parcial: ${dados.sem_classificacao} aluno(s) sem classificação para este indicador.`"></el-alert>

              <div v-if="dados.situacao === 'sem_dados'" class="u-fs-12">Sem dados para este indicador.</div>

              <div v-else>
                <!-- Gráfico SVG nativo: sem dependência externa, como no portal do aluno -->
                <svg :viewBox="`0 0 300 ${dados.faixas.length * 26 + 10}`" style="width:100%;height:auto;">
                  <g v-for="(f, i) in dados.faixas" :key="f.classificacao">
                    <rect x="0" :y="i * 26 + 4" :width="larguraBarra(f, dados)" height="16"
                          :fill="cor(f.classificacao)" rx="2"></rect>
                    <text x="4" :y="i * 26 + 16" font-size="10" fill="#212121">
                      {{ f.classificacao }} — {{ f.quantidade }} ({{ f.percentual }}%)
                    </text>
                  </g>
                </svg>

                <div class="u-fs-12 u-mt-5">
                  classificados: {{ dados.classificados }} ·
                  não aplicável: {{ dados.nao_aplicavel }} ·
                  sem classificação: {{ dados.sem_classificacao }}
                </div>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- ITEM 33 -->
      <h4 class="u-mt-30 u-mb-10">Aniversariantes do dia</h4>
      <div v-if="aniversariantes && aniversariantes.total === 0" class="u-fs-12">
        Nenhum aniversariante hoje no seu escopo.
      </div>
      <el-row :gutter="10" v-else-if="aniversariantes">
        <el-col :xs="12" :sm="6" v-for="a in aniversariantes.aniversariantes" :key="a.aluno_id">
          <div class="card-indicador card-aniversariante">
            <!-- MER 28 — identificação visual também aqui, pela rota autenticada. -->
            <foto-pessoa endpoint="api/merenda/aluno-foto" :id="a.aluno_id"
                         :nome="a.nome" :tamanho="44"></foto-pessoa>
            <div class="ani-dados">
              <div>{{ a.nome }}</div>
              <div class="u-fs-12">{{ a.turma || '—' }} · {{ a.idade }} anos</div>
            </div>
          </div>
        </el-col>
      </el-row>
    </c-panel>
  </div>
</template>

<script>
import FotoPessoa from '@/shared/foto-pessoa/foto-pessoa.vue'

export default {
  components: { FotoPessoa },
  data: () => ({
    instituicoes: [], filtro: { instituicao_id: '' },
    alunos: null, nutricional: null, aniversariantes: null,
    rotulos: { estatura_idade: 'Estatura × idade', imc_idade: 'IMC × idade', peso_idade: 'Peso × idade' }
  }),
  computed: {
    /* Reflete exatamente o backend: `valor` null vira "sem dados", nunca zero. */
    cards () {
      if (!this.alunos || !this.alunos.total_alunos) return []
      const a = this.alunos
      return [
        { chave: 'total', rotulo: 'Total de alunos', valor: a.total_alunos.valor, percentual: null },
        { chave: 'masc', rotulo: 'Masculino', valor: a.por_sexo.masculino.valor, percentual: a.por_sexo.masculino.percentual },
        { chave: 'fem', rotulo: 'Feminino', valor: a.por_sexo.feminino.valor, percentual: a.por_sexo.feminino.percentual },
        { chave: 'def', rotulo: 'Com deficiência', valor: a.com_deficiencia.valor, percentual: a.com_deficiencia.percentual },
        { chave: 'restr', rotulo: 'Com restrição alimentar', valor: a.com_restricao_alimentar.valor, percentual: a.com_restricao_alimentar.percentual }
      ]
    }
  },
  mounted () { this.carregarInstituicoes(); this.carregar() },
  methods: {
    carregarInstituicoes () {
      this.$axios.get(`${this.$store.state.config.baseUrlApi}/api/instituicao/getAll`, this.$store.getters.getHeaderAuth)
        .then(r => { this.instituicoes = Array.isArray(r.data) ? r.data : (r.data.data || []) })
        .catch(() => { this.instituicoes = [] })
    },
    carregar () {
      const h = this.$store.getters.getHeaderAuth
      const base = this.$store.state.config.baseUrlApi
      this.$axios.post(`${base}/api/merenda/painel/indicadores-alunos`, this.filtro, h)
        .then(r => { this.alunos = r.data }).catch(e => this.$store.commit('throwException', e))
      this.$axios.post(`${base}/api/merenda/painel/situacao-nutricional`, this.filtro, h)
        .then(r => { this.nutricional = r.data }).catch(() => { this.nutricional = null })
      this.$axios.post(`${base}/api/merenda/painel/aniversariantes`, this.filtro, h)
        .then(r => { this.aniversariantes = r.data }).catch(() => { this.aniversariantes = null })
    },
    larguraBarra (f, dados) {
      const max = Math.max.apply(null, dados.faixas.map(x => x.quantidade)) || 1
      return Math.max(2, Math.round(f.quantidade * 296 / max))
    },
    cor (classificacao) {
      const c = (classificacao || '').toLowerCase()
      if (c.indexOf('acentuad') !== -1 || c.indexOf('muito baix') !== -1) return '#c62828'
      if (c.indexOf('magreza') !== -1 || c.indexOf('baix') !== -1) return '#ef6c00'
      if (c.indexOf('eutrofia') !== -1 || c.indexOf('adequad') !== -1) return '#2e7d32'
      if (c.indexOf('risco') !== -1) return '#f9a825'
      if (c.indexOf('sobrepeso') !== -1) return '#ef6c00'
      if (c.indexOf('obesidade') !== -1) return '#c62828'
      return '#607d8b'
    }
  }
}
</script>

<style scoped>
.card-indicador { border: 1px solid #e0e0e0; border-radius: 4px; padding: 10px; margin-bottom: 10px; }
.card-indicador .valor { font-size: 22px; font-weight: 600; }
.bloco-indicador { border: 1px solid #e0e0e0; border-radius: 4px; padding: 10px; margin-bottom: 10px; }

.card-aniversariante { display: flex; align-items: center; gap: 10px; }
.ani-dados { min-width: 0; }
.ani-dados > div { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
